import {
  DurationPickerV2 as DurationPicker,
  FieldError,
  NumberField,
} from '@automata/ui';
import {
  castTimeBreakdownStrings,
  getTimeBreakdownStrings,
  getTotalSeconds,
} from '@automata/ui/src/DurationPickerV2/utils';
import {
  BooleanParameterDefinition,
  FileRefParameterDefinition,
  IntegerParameterDefinition,
  ParameterDefinition,
  StringParameterDefinition,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { FormControlLabel, Switch, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';

import { Errors, NumberParameterDefinition, OnChangeFn } from './types';
import { TextFieldWithMaxLength } from '../../TextFieldWithMaxLength';

interface ParameterFieldsProps<T = ParameterDefinition> {
  errors?: Errors;
  parameter: T;
  onChange: OnChangeFn<T>;
}

export const NumericParameterFields = ({
  errors,
  parameter,
  onChange,
}: ParameterFieldsProps<NumberParameterDefinition>) => {
  const isFloat = parameter.type === 'float';

  return (
    <Stack direction="row" justifyContent="flex-end" spacing={1}>
      <NumberField
        error={errors?.minimum?.message}
        decimal={isFloat}
        label="min value"
        value={parameter.minimum}
        onChange={(e) => onChange('minimum', e)}
      />
      <NumberField
        error={errors?.default?.message}
        decimal={isFloat}
        label="default value"
        value={typeof parameter.default === 'number' ? parameter.default : null}
        onChange={(e) => onChange('default', e)}
      />
      <NumberField
        error={errors?.maximum?.message}
        decimal={isFloat}
        label="max value"
        value={parameter.maximum}
        onChange={(e) => onChange('maximum', e)}
      />
    </Stack>
  );
};

export const StringParameterFields = ({
  errors,
  parameter,
  onChange,
}: ParameterFieldsProps<StringParameterDefinition>) => {
  return (
    <>
      <TextFieldWithMaxLength
        error={!!errors?.default}
        label="default value"
        fullWidth
        value={typeof parameter.default === 'string' ? parameter.default : ''}
        onChange={(e) => onChange('default', e.target.value)}
      />
      <FieldError error={errors?.default?.message} />
    </>
  );
};

export const BooleanParameterFields = ({
  parameter,
  onChange,
}: ParameterFieldsProps<BooleanParameterDefinition>) => {
  return (
    <Stack>
      <Typography variant="overline">default value</Typography>
      <FormControlLabel
        control={
          <Switch
            id="canvas-parameterReference-switch"
            checked={
              typeof parameter.default === 'boolean' ? parameter.default : false
            }
            onChange={() => onChange('default', !parameter.default)}
          />
        }
        label={parameter.default ? 'True' : 'False'}
      />
    </Stack>
  );
};

export const DurationParameterFields = ({
  errors,
  parameter,
  onChange,
}: ParameterFieldsProps<IntegerParameterDefinition>) => {
  const { hours, minutes, seconds } = getTimeBreakdownStrings(
    typeof parameter.default === 'number' ? parameter.default : undefined
  );

  return (
    <>
      <DurationPicker
        error={!!errors?.default}
        label="Duration"
        hours={hours}
        minutes={minutes}
        seconds={seconds}
        handleUpdateTime={(timePatch) => {
          const totalSeconds = getTotalSeconds(
            castTimeBreakdownStrings({
              hours,
              minutes,
              seconds,
              ...timePatch,
            })
          );
          onChange('default', totalSeconds >= 0 ? totalSeconds : null);
        }}
      />
      <FieldError error={errors?.default?.message} />
    </>
  );
};

export const FileParameterFields = ({
  errors,
  parameter,
  onChange,
}: ParameterFieldsProps<FileRefParameterDefinition>) => {
  return (
    <Box>
      <TextFieldWithMaxLength
        label="file name"
        error={!!errors?.default?.message}
        fullWidth
        value={typeof parameter.default === 'string' ? parameter.default : ''}
        onChange={(e) => onChange('default', e.target.value)}
      />
      <FieldError error={errors?.default?.message} />
    </Box>
  );
};
